import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import EMOJI_REGEX from 'emojibase-regex/emoji';
import EMOTICON_REGEX from 'emojibase-regex/emoticon';
import SHORTCODE_REGEX from 'emojibase-regex/shortcode';
import escapeStringRegex from 'escape-string-regexp';
import { Notomoji, Openmoji, Twemoji, Blobmoji, SpriteCollection } from 'svgmoji';
import { extension, command, keyBinding, NodeExtension, isString, ExtensionTag, isElementDomNode, omitExtraAttributes, plainInputRule, nodeInputRule, getTextSelection, LEAF_NODE_REPLACING_CHARACTER, getMatchString } from '@remirror/core';
import { ExtensionEmojiTheme } from '@remirror/theme';

var DefaultMoji = {
  /**
   * The google emoji library.
   */
  noto: Notomoji,

  /**
   * The openmoji library.
   */
  openmoji: Openmoji,

  /**
   * The Twitter emoji library,
   */
  twemoji: Twemoji,

  /**
   * The blob emoji library previously developed by google. Now a community project.
   */
  blob: Blobmoji
};
var EMOJI_DATA_ATTRIBUTE = 'data-remirror-emoji';

var _dec, _dec2, _dec3, _dec4, _class, _class2;
var EmojiExtension = (_dec = extension({
  defaultOptions: {
    plainText: false,
    data: [],
    identifier: 'emoji',
    fallback: ':red_question_mark:',
    moji: 'noto',
    suggestionCharacter: ':'
  },
  staticKeys: ['plainText'],
  handlerKeys: ['suggestEmoji']
}), _dec2 = command(), _dec3 = command(), _dec4 = keyBinding({
  shortcut: 'Enter'
}), _dec(_class = (_class2 = class EmojiExtension extends NodeExtension {
  /**
   * The name is dynamically generated based on the passed in type.
   */
  get name() {
    return 'emoji';
  }

  get moji() {
    var _this$_moji;

    return (_this$_moji = this._moji) !== null && _this$_moji !== void 0 ? _this$_moji : this._moji = isString(this.options.moji) ? new DefaultMoji[this.options.moji]({
      data: this.options.data,
      type: SpriteCollection.All,
      fallback: this.options.fallback
    }) : this.options.moji;
  }

  createTags() {
    return [ExtensionTag.InlineNode];
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({
      selectable: true,
      draggable: false
    }, override), {}, {
      inline: true,
      atom: true,
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        code: {}
      }),
      parseDOM: [{
        tag: "span[".concat(EMOJI_DATA_ATTRIBUTE),
        getAttrs: node => {
          if (!isElementDomNode(node)) {
            return;
          }

          var code = node.getAttribute(EMOJI_DATA_ATTRIBUTE);
          return _objectSpread(_objectSpread({}, extra.parse(node)), {}, {
            code
          });
        }
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => {
        var _this$moji$find;

        var _ref = omitExtraAttributes(node.attrs, extra),
            code = _ref.code;

        var emoji = (_this$moji$find = this.moji.find(code)) !== null && _this$moji$find !== void 0 ? _this$moji$find : this.moji.fallback;
        return ['span', {
          class: ExtensionEmojiTheme.EMOJI_WRAPPER,
          [EMOJI_DATA_ATTRIBUTE]: emoji[this.options.identifier]
        }, ['img', {
          role: 'presentation',
          class: ExtensionEmojiTheme.EMOJI_IMAGE,
          'aria-label': emoji.annotation,
          alt: emoji.annotation,
          // TODO use the emoji rather than the code once `svgmoji` supports it.
          src: this.moji.url(code)
        }] // ['span', { style: 'display: inline-block; text-indent: -99999px' }, emoji.emoji],
        ];
      }
    });
  }
  /**
   * Manage input rules for emoticons.
   */


  createInputRules() {
    // Use plain text when this option is set.
    if (this.options.plainText) {
      return [// Replace emoticons
      plainInputRule({
        regexp: new RegExp("(".concat(EMOTICON_REGEX.source, ")[\\s]$")),
        transformMatch: _ref2 => {
          var _ref3 = _slicedToArray(_ref2, 2),
              full = _ref3[0],
              partial = _ref3[1];

          if (!full || !partial) {
            return null;
          }

          var emoji = this.moji.find(partial);
          return emoji ? full.replace(partial, emoji.emoji) : null;
        }
      }), // Replace matching shortcodes
      plainInputRule({
        regexp: new RegExp("(".concat(SHORTCODE_REGEX.source, ")$")),
        transformMatch: _ref4 => {
          var _ref5 = _slicedToArray(_ref4, 2),
              match = _ref5[1];

          if (!match) {
            return null;
          }

          var emoji = this.moji.find(match);
          return emoji ? emoji.emoji : null;
        }
      })];
    } // Return true when the input rule should be skipped.


    var shouldSkip = _ref6 => {
      var captureGroup = _ref6.captureGroup;
      // eslint-disable-next-line unicorn/prefer-array-some
      return !captureGroup || !this.moji.find(captureGroup);
    }; // Capture the attributes for the emoji


    var getAttributes = _ref7 => {
      var _ref8 = _slicedToArray(_ref7, 2),
          match = _ref8[1];

      if (!match) {
        return;
      }

      var emoji = this.moji.find(match);
      return emoji ? {
        code: emoji[this.options.identifier]
      } : undefined;
    }; // The current emoji node type.


    var type = this.type;
    return [// Replace emoticons
    nodeInputRule({
      type,
      shouldSkip,
      getAttributes,
      regexp: new RegExp("(".concat(EMOTICON_REGEX.source, ")[\\s]$")),
      beforeDispatch: _ref9 => {
        var tr = _ref9.tr;
        tr.insertText(' ');
      }
    }), // Replace matching shortcodes
    nodeInputRule({
      type,
      shouldSkip,
      getAttributes,
      regexp: new RegExp("(".concat(SHORTCODE_REGEX.source, ")$"))
    }), // Replace matching shortcodes
    nodeInputRule({
      type,
      shouldSkip,
      getAttributes,
      regexp: new RegExp("(".concat(EMOJI_REGEX.source, ")"))
    })];
  }
  /**
   * Insert an emoji into the document at the requested location by name
   *
   * The range is optional and if not specified the emoji will be inserted
   * at the current selection.
   *
   * @param identifier - the hexcode | unicode | shortcode | emoticon of the emoji to insert.
   * @param [options] - the options when inserting the emoji.
   */


  addEmoji(identifier) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return props => {
      var _options$selection;

      var dispatch = props.dispatch,
          tr = props.tr;
      var emoji = this.moji.find(identifier);

      if (!emoji) {
        // Nothing to do here since no emoji found.
        return false;
      }

      if (!this.options.plainText) {
        return this.store.commands.replaceText.original({
          type: this.type,
          attrs: {
            code: emoji[this.options.identifier]
          },
          selection: options.selection
        })(props);
      }

      var _getTextSelection = getTextSelection((_options$selection = options.selection) !== null && _options$selection !== void 0 ? _options$selection : tr.selection, tr.doc),
          from = _getTextSelection.from,
          to = _getTextSelection.to;

      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.insertText(emoji.emoji, from, to));
      return true;
    };
  }
  /**
   * Inserts the suggestion character into the current position in the
   * editor in order to activate the suggestion popup.
   */


  suggestEmoji(selection) {
    return _ref10 => {
      var tr = _ref10.tr,
          dispatch = _ref10.dispatch;

      var _getTextSelection2 = getTextSelection(selection !== null && selection !== void 0 ? selection : tr.selection, tr.doc),
          from = _getTextSelection2.from,
          to = _getTextSelection2.to;

      var text = this.store.helpers.getTextBetween(from - 1, to, tr.doc);

      if (text.includes(this.options.suggestionCharacter)) {
        return false;
      }

      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.insertText(this.options.suggestionCharacter, from, to));
      return true;
    };
  }

  handleEnterKey(_ref11) {
    var tr = _ref11.tr,
        next = _ref11.next;
    var _tr$selection = tr.selection,
        $from = _tr$selection.$from,
        empty = _tr$selection.empty;

    if (!empty) {
      return next();
    } // Try and find an emoticon in the last 5 characters


    var textBeforeCursor = $from.parent.textBetween(Math.max(0, $from.parentOffset - 5), $from.parentOffset, undefined, LEAF_NODE_REPLACING_CHARACTER);
    var match = textBeforeCursor.match(EMOTICON_REGEX);

    if (match) {
      var emoticon = getMatchString(match);
      var selection = {
        from: $from.pos - emoticon.length,
        to: $from.pos
      }; // Replace the matching text with the emoticon

      this.store.chain(tr).addEmoji(emoticon, {
        selection
      }).tr();
    }

    return next();
  }
  /**
   * Emojis can be selected via `:` the colon key (by default). This sets the
   * configuration using `prosemirror-suggest`
   */


  createSuggesters() {
    return {
      disableDecorations: true,
      invalidPrefixCharacters: "".concat(escapeStringRegex(this.options.suggestionCharacter), "|\\w"),
      char: this.options.suggestionCharacter,
      name: this.name,
      suggestTag: 'span',
      onChange: props => {
        // When the change handler is called call the extension handler
        // `suggestEmoji` with props that can be used to trigger the emoji.
        this.options.suggestEmoji({
          moji: this.moji,
          query: props.query.full,
          text: props.text.full,
          range: props.range,
          exit: !!props.exitReason,
          change: !!props.changeReason,
          apply: code => {
            this.store.commands.addEmoji(code, {
              selection: props.range
            });
          }
        });
      }
    };
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "addEmoji", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "addEmoji"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "suggestEmoji", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "suggestEmoji"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "handleEnterKey", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "handleEnterKey"), _class2.prototype)), _class2)) || _class);

export { EmojiExtension };
